import React, { ReactNode, useCallback } from "react";

import { StyledButton, StyledPopover } from "components/StyledUI";
import type { StyledPopoverProps } from "components/StyledUI";

import { ReactComponent as ArrowIcon } from "assets/icons/arrow-down-1.svg";
import { tss } from "tss-react/mui";
import { ButtonProps } from "@mui/material";

const useStyles = tss.create(({ theme }) => ({
  button: {
    justifyContent: "space-between",
    textTransform: "none",
    fontWeight: 400,
    lineHeight: "20px",
    fontSize: 14,
    letterSpacing: ".5px",
    borderWidth: "2px",
    borderStyle: "solid",
    borderColor: theme.palette.grey[50],
    backgroundColor: theme.palette.grey[50],
    color: theme.palette.text.primary,
    "& svg": {
      color: theme.palette.grey[800],
      height: 16,
      width: 16,
      pointerEvents: "none",
    },
    "& .MuiButton-endIcon": {
      marginLeft: 8,
      marginRight: 0,
    },
    "&:hover": {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.grey[50],
      color: theme.palette.text.primary,
      "& svg": {
        color: theme.palette.grey[800],
      },
    },
    "&:focus": {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.common.white,
      color: theme.palette.text.primary,
      "& svg": {
        color: theme.palette.grey[800],
        pointerEvents: "none",
      },
    },
  },
  open: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.common.white,
    "& svg": {
      transform: "rotateZ(180deg)",
      pointerEvents: "none",
    },
  },
  dark: {
    borderColor: theme.palette.grey[800],
    backgroundColor: theme.palette.grey[800],
    color: theme.palette.grey[50],
    textAlign: "start",
    "& svg": {
      color: theme.palette.grey[50],
    },
    "&:hover": {
      backgroundColor: theme.palette.grey[800],
      color: theme.palette.grey[50],
      "& svg": {
        color: theme.palette.grey[50],
      },
    },
    "&:focus": {
      backgroundColor: theme.palette.text.primary,
      color: theme.palette.common.white,
      "& svg": {
        color: theme.palette.grey[50],
      },
    },
  },
  openDark: {
    backgroundColor: theme.palette.text.primary,
  },
  popover: {
    "& .MuiPopover-paper": {
      marginTop: 0,
      maxHeight: "228px",
    },
    "& .MuiFormControlLabel-root": {
      display: "flex",
      padding: 4,
      "&:hover": {
        borderRadius: theme.shape.borderRadius,
        backgroundColor: theme.palette.grey[50],
      },
    },
    "& .MuiTextField-root": {
      marginBottom: 8,
    },
  },
  popoverDark: {
    "& .MuiMenuItem-root": {
      color: theme.palette.common.white,
    },
  },
}));

export type StyledFilterDropdownProps = {
  buttonClassName?: string;
  buttonLabel: ReactNode;
  disabled?: boolean;
  fullWidth?: boolean;
  onEnter?: (node: HTMLElement, isAppearing: boolean) => void;
} & Omit<StyledPopoverProps, "open"> &
  Pick<ButtonProps, "size">;

const StyledFilterDropdown = React.forwardRef<
  HTMLDivElement,
  StyledFilterDropdownProps
>(
  (
    {
      buttonClassName,
      buttonLabel,
      children,
      dark,
      disabled,
      fullWidth,
      onEnter,
      size,
      style,
      ...rest
    },
    ref
  ) => {
    const { classes, cx } = useStyles();
    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement>();

    const handleClick = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
      },
      []
    );

    const handleClose = useCallback(() => {
      setAnchorEl(undefined);
    }, []);

    const handleChildrenClick: React.MouseEventHandler<HTMLDivElement> =
      useCallback((event) => {
        const { target } = event;
        if (target instanceof HTMLElement && target.dataset.closeOnClick) {
          setAnchorEl(undefined);
        }
      }, []);

    const open = anchorEl != null;
    const id = open ? "filter-dropdown" : undefined;

    return (
      <>
        <StyledButton
          className={cx(classes.button, buttonClassName, {
            [classes.dark]: dark,
            [classes.open]: open,
            [classes.openDark]: open && dark,
          })}
          aria-describedby={id}
          variant="outlined"
          size={size}
          fullWidth={fullWidth}
          style={style}
          endIcon={<ArrowIcon />}
          onClick={handleClick}
          disabled={disabled}>
          {buttonLabel}
        </StyledButton>

        <StyledPopover
          className={cx(classes.popover, { [classes.popoverDark]: dark })}
          fullwidth={fullWidth}
          id={id}
          open={open}
          anchorEl={anchorEl}
          ref={ref}
          onClose={handleClose}
          onClick={handleChildrenClick}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          TransitionProps={{
            onEnter,
          }}
          dark={dark}
          {...rest}>
          {children}
        </StyledPopover>
      </>
    );
  }
);

export { StyledFilterDropdown };
